import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ScanScreen from '../screens/ScanScreen'
import { UiState, getOnClientSwitch } from '../managers/UIManager'
import { getEnabledClients } from '../config/clients'

interface Props {
  state: UiState
  setTab: (tab: UiState['activeTab']) => void
  clientId: string
  title: string
}

export default function CollectionCameraPage({ state, setTab, clientId, title }: Props) {
  const navigate = useNavigate()
  const [showFeatured, setShowFeatured] = useState(true)

  const client = getEnabledClients().find((c) => c.clientId === clientId)
  const featured = (client as any)?.featured

  useEffect(() => {
    const onSwitch = getOnClientSwitch()
    if (state.activeClientId !== clientId) {
      onSwitch?.(clientId)
    }
  })

  // Stop AR audio when this page unmounts (user navigates away)
  useEffect(() => {
    return () => {
      document.dispatchEvent(new CustomEvent('arScreenUnmounted'))
    }
  }, [])

  const goHome = () => navigate('/')

  const goScan = () => {
  setTab('scan')
  navigate('/')
  }

  return (
    <div className="collection-camera-page" aria-label={`${title} camera page`}>
      {/* AR scan layer */}
      <ScanScreen state={state} setTab={setTab} mode="collection" />

      {/* Featured banner */}
      {showFeatured && featured && (
        <div className="collection-featured-banner">
          <div className="cfb-content">
            <span className="cfb-label">FEATURED</span>
            <div className="cfb-title">{featured.title}</div>
            <div className="cfb-desc">{featured.description}</div>
            {featured.link && (
              <button
                className="cfb-cta"
                onClick={() => window.open(featured.link, '_blank', 'noopener,noreferrer')}
              >
                Learn More
              </button>)}
          </div>
          {featured.logoSrc && (
            <img src={featured.logoSrc} alt={title} className="cfb-logo" />
          )}
          <button className="cfb-close" onClick={() => setShowFeatured(false)} aria-label="Close featured">
            X
          </button>
        </div>
      )}

      {!showFeatured && featured && (
        <button
          className="cfb-reopen-tab"
          onClick={() => setShowFeatured(true)}
          aria-label="Show featured info"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </button>
      )}

      {/* Bottom navigation — all tabs return to home */}
      <nav className="collection-bottom-nav">
        <button className="cbn-item" onClick={goHome} aria-label="Home">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          <span>Home</span>
        </button>

        <button className="cbn-item" onClick={goHome} aria-label="Explore">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <span>Explore</span>
        </button>

        <button className="cbn-item cbn-scan-item" onClick={goScan} aria-label="Scan">
          <div className="cbn-scan-circle">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
          </div>
          <span>Scan</span>
        </button>

        <button className="cbn-item" onClick={goHome} aria-label="Shop">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <path d="M16 10a4 4 0 0 1-8 0" />
          </svg>
          <span>Shop</span>
        </button>

        <button className="cbn-item" onClick={goHome} aria-label="Profile">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <span>Profile</span>
        </button>
      </nav>
    </div>
  )
}
